/**
 * Recover from stale-deploy chunk-load errors.
 *
 * After a redeploy, an already-open tab (or a cached index.html) references old
 * hashed chunk filenames that no longer exist on the host. Vercel's SPA rewrite
 * then serves index.html (text/html) for the missing *.js, so the lazy route's
 * dynamic import() rejects with "'text/html' is not a valid JavaScript MIME
 * type". Vite dispatches a `vite:preloadError` event for exactly this case; we
 * reload once to pull the fresh index.html and its new chunk hashes.
 */

const STORAGE_KEY = 'nd:last-chunk-reload';
const RELOAD_DEBOUNCE_MS = 10_000;

/**
 * Reload only if we haven't already reloaded within `debounceMs`. Guards against
 * an infinite reload loop when the chunk is genuinely gone (a broken build), not
 * merely stale: after one reload it still fails, so we stop and let the error
 * bubble to the ErrorBoundary instead of looping forever.
 */
export function shouldReloadOnChunkError(
  now: number,
  lastReloadMs: number,
  debounceMs: number = RELOAD_DEBOUNCE_MS,
): boolean {
  return now - lastReloadMs > debounceMs;
}

export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', () => {
    const last = Number(sessionStorage.getItem(STORAGE_KEY) ?? 0);
    if (shouldReloadOnChunkError(Date.now(), last)) {
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
      window.location.reload();
    }
    // else: within the debounce window → let Vite's default throw reach the
    // ErrorBoundary rather than reload-looping on a genuinely missing chunk.
  });
}
